<script lang="ts">
import { isAppUsing } from '@@/bits/flip'
import { WallWishGroupByTypes } from '@@/enums'
import OzDropIndicatorLine from '@@/library/v4/components/OzDropIndicatorLine.vue'
import { useSurfaceStore } from '@@/pinia/surface'
import { useSurfacePostsStore } from '@@/pinia/surface_posts'
import { useSurfacePostsDragAndDropStore } from '@@/pinia/surface_posts_drag_and_drop'
import { useSurfaceSectionsStore } from '@@/pinia/surface_sections'
import { useSurfaceWishArrangementStore } from '@@/pinia/surface_wish_arrangement'
import { useWindowSizeStore } from '@@/pinia/window_size'
import type { LogicalEdge, OnDropPayload } from '@@/pragmatic-drag-and-drop/helpers'
import { useDraggableElement } from '@@/pragmatic-drag-and-drop/useDraggableElement'
import { useDropTargetElement } from '@@/pragmatic-drag-and-drop/useDropTargetElement'
import { computePostIndexFromPost } from '@@/surface/sorter'
import type { Section, Id, Post } from '@@/types'
import SurfaceLazy from '@@/vuecomponents/SurfaceLazy.vue'
import SurfacePostGroupHeaderSection from '@@/vuecomponents/SurfacePostGroupHeaderSection.vue'
import SurfacePostGroupHeaderSectionOld from '@@/vuecomponents/SurfacePostGroupHeaderSectionOld.vue'
import SurfacePostsStreamSectionHeaderDragPreview from '@@/vuecomponents/SurfacePostsStreamSectionHeaderDragPreview.vue'
import SurfacePost from '@@/vuecomponents/surface_post.vue'
import { useSectionSorterHelper } from '@@/vuecomposables/section_sorter_helper'
import { useDragSortableInGroups } from '@@/vuecomposables/surface_posts_drag_sortable_in_groups'
import { storeToRefs } from 'pinia'
import { computed, ref } from 'vue'
import type { Group } from '@@/bits/surface_posts_grouping_helper'
import { useSurfacePermissionsStore } from '@@/pinia/surface_permissions'
import { usePostSorter } from '@@/vuecomposables/surface_post_sorter'
import SurfacePostDraggable from '@@/vuecomponents/SurfacePostDraggable.vue'

export const SURFACE_STREAM_POST_DRAGGABLE_TYPE = 'surface_stream_post'
export const SURFACE_STREAM_SECTION_HEADER_DRAGGABLE_TYPE = 'surface_stream_section_header'
export default {}
</script>

<script setup lang="ts">
const props = withDefaults(
  defineProps<{
    group: Group
    reactionIdsByPostId: Record<Id, Id[]>
    commentIdsByPostId: Record<Id, Id[]>
  }>(),
  {
    reactionIdsByPostId: () => ({}),
    commentIdsByPostId: () => ({}),
  },
)

const surfacePostsStore = useSurfacePostsStore()
const surfacePostsDragAndDropStore = useSurfacePostsDragAndDropStore()
const { canIDragSections } = storeToRefs(useSurfacePermissionsStore())
const { backgroundLuminance, isPostSizeStandard, isPostSizeWide } = storeToRefs(useSurfaceStore())
const {
  isAPostDragging,
  groupIdUnderPostBeingDragged,
  shouldDisableDraggingPost,
  isDraggingPostInNonManuallySortedWall,
} = storeToRefs(surfacePostsDragAndDropStore)
const { allSortedPostsByGroup } = storeToRefs(surfacePostsStore)
const { wishGroupBy, isGroupByNone } = storeToRefs(useSurfaceWishArrangementStore())
const windowSizeStore = useWindowSizeStore()
const { isSmallerThanTabletPortrait, windowHeight, windowWidth } = storeToRefs(windowSizeStore)

const { shouldDisplayPost } = surfacePostsStore
const { canShowDraggingOverlayOnGroup } = surfacePostsDragAndDropStore

const overlayHeight = computed(() => {
  const hoveredSectionQuery = `#section-${groupIdUnderPostBeingDragged.value}`
  const hoveredSectionElementHeight = document.querySelector(hoveredSectionQuery)?.getBoundingClientRect().height
  // add extra height to the overlay so it extends beyond posts and section header
  return hoveredSectionElementHeight ? hoveredSectionElementHeight + 32 : 0
})

const overlayWidth = computed(() => {
  const postElementWidth = document.querySelector('.wish-v2')?.getBoundingClientRect().width
  // add extra width to the overlay so it extends beyond posts
  return postElementWidth ? postElementWidth + 16 : 0
})

// If the feature flag is off, we use the old drag and drop for posts
if (!isAppUsing('pdndSurfaceStream')) {
  useDragSortableInGroups({
    onDragStart: ({ groupId }) => {
      isAPostDragging.value = true
      groupIdUnderPostBeingDragged.value = groupId
    },
    onDragEnd: ({ postCid, postId, groupId }) => {
      isAPostDragging.value = false
      if (postCid && postId) {
        const sectionId =
          typeof groupId === 'number' && wishGroupBy.value === WallWishGroupByTypes.Section ? groupId : undefined
        const prevWish = document
          .querySelector(`#wish-${postId}`)
          ?.closest('.wish-wrapper')
          ?.previousElementSibling?.querySelector('.wish') as HTMLElement | undefined
        const nextWish = document
          .querySelector(`#wish-${postId}`)
          ?.closest('.wish-wrapper')
          ?.nextElementSibling?.querySelector('.wish') as HTMLElement | undefined
        const { sortIndex, gapSize } = computePostIndexFromPost(`#wish-${postId}`, {
          sectionId,
          prevId: prevWish?.id,
          nextId: nextWish?.id,
        })
        surfacePostsStore.sortPost({
          postCid,
          sortIndex: sortIndex ?? undefined,
          sectionId,
          gapSize,
        })
      }
      groupIdUnderPostBeingDragged.value = undefined
    },
    format: 'stream',
  })
}

const minHeight = computed(() => {
  // Set minHeight as 1/4 of windowHeight since we assume that each post fills 1/4 of the screen's height
  return windowHeight.value / 4
})
const minWidth = computed(() => {
  const minimumPostWidthStandard = 548
  const minimumPostWidthWide = 748
  return isPostSizeStandard.value ? minimumPostWidthStandard : minimumPostWidthWide
})

// #region Drag and drop for section header
const { isASectionDragging } = storeToRefs(useSurfaceSectionsStore())
const groupHeaderSectionRef = ref<
  InstanceType<typeof SurfacePostGroupHeaderSection> | InstanceType<typeof SurfacePostGroupHeaderSectionOld>
>()
const sectionTitleRef = computed<HTMLElement | undefined>(() => {
  if (groupHeaderSectionRef.value === undefined) {
    return undefined
  }
  return groupHeaderSectionRef.value?.sectionTitleRef?.$el as HTMLElement
})
const groupIdBeingDragged = ref<string | number | undefined>(undefined)
const isCurrentSectionBeingDragged = computed(() => groupIdBeingDragged.value === props.group.id)
useDraggableElement({
  elementRef: sectionTitleRef,
  type: SURFACE_STREAM_SECTION_HEADER_DRAGGABLE_TYPE,
  data: computed(() => {
    return { section: props.group as Section }
  }),
  onDragStart: () => {
    isASectionDragging.value = true
    groupIdBeingDragged.value = props.group.id
  },
  onDrop: () => {
    isASectionDragging.value = false
    groupIdBeingDragged.value = undefined
  },
  dragPreview: SurfacePostsStreamSectionHeaderDragPreview,
  dragPreviewComponentProps: computed(() => {
    return {
      section: props.group as Section,
    }
  }),
  isDraggable: canIDragSections,
})
const { closestEdgeToPointer: closestEdgeToPointerForSectionHeader, addDraggableSource } = useDropTargetElement({
  elementRef: sectionTitleRef,
  type: SURFACE_STREAM_SECTION_HEADER_DRAGGABLE_TYPE,
  data: computed(() => {
    return { section: props.group as Section }
  }),
})
addDraggableSource({
  type: SURFACE_STREAM_SECTION_HEADER_DRAGGABLE_TYPE,
  axis: 'vertical',
  onDrop: (e: OnDropPayload<{ section: Section }, { section: Section }>) => {
    reorderSections(e)
    isASectionDragging.value = false
    groupIdBeingDragged.value = undefined
  },
})
function reorderSections(e): void {
  const sourceSection = e.sourceItem.data.section as Section
  const targetSection = e.targetItem.data.section as Section
  if (typeof targetSection.id !== 'number') return
  const { sortIndex, gapSize } = useSectionSorterHelper().getSortIndexAndGapSize(
    targetSection.id,
    e.relativePositionToTarget,
  )

  if (sortIndex === undefined || gapSize === undefined) return

  useSurfaceSectionsStore().sortSection({
    sectionId: Number(sourceSection.id),
    sortIndex,
    gapSize,
  })
}
// #endregion

// #region Drag and drop for posts
function handlePostDragStart(): void {
  isAPostDragging.value = true
  groupIdUnderPostBeingDragged.value = props.group.id
}
function handlePostDrop(e: OnDropPayload<{ post: Post }, { post: Post }>): void {
  isAPostDragging.value = false
  groupIdUnderPostBeingDragged.value = undefined
  const targetPost = e.targetItem.data.post
  const sourcePost = e.sourceItem.data.post
  const posts = allSortedPostsByGroup.value[props.group.id]
  if (!posts) return
  const targetIndex = posts.findIndex((post) => post.cid === targetPost.cid)
  let prevPost: Post | undefined
  let nextPost: Post | undefined
  if (e.relativePositionToTarget === 'before') {
    prevPost = posts[targetIndex - 1]
    nextPost = posts[targetIndex]
  } else {
    prevPost = posts[targetIndex]
    nextPost = posts[targetIndex + 1]
  }
  const { sortIndex, gapSize } = usePostSorter().getNewIndexBetween(prevPost, nextPost)
  const sectionId = Number(props.group.id)
  // Update new post order to server
  surfacePostsStore.sortPost({
    postCid: sourcePost.cid,
    sortIndex,
    sectionId,
    gapSize,
  })
}
// #endregion

// #region Drag and drop for posts into section
const sectionElementRef = ref<HTMLElement | undefined>(undefined)
// The edge should be bottom if the wall is non-manually sorted, and undefined if the wall is manually sorted
const closestEdgeToPointerForSection = ref<LogicalEdge | undefined>(undefined)
const { addDraggableSource: addDraggableSourceForSection } = useDropTargetElement({
  elementRef: sectionElementRef,
  type: SURFACE_STREAM_SECTION_HEADER_DRAGGABLE_TYPE,
  data: computed(() => {
    return { section: props.group as Section }
  }),
})
addDraggableSourceForSection({
  type: SURFACE_STREAM_POST_DRAGGABLE_TYPE,
  axis: 'vertical',
  onDrop: (e: OnDropPayload<{ post: Post }, { section: Section }>) => {
    isAPostDragging.value = false
    groupIdUnderPostBeingDragged.value = undefined
    closestEdgeToPointerForSection.value = undefined
    const sourcePost = e.sourceItem.data.post
    const targetGroupId = e.targetItem.data.section.id
    surfacePostsStore.updatePostSection({
      postCid: sourcePost.cid,
      sectionId: targetGroupId,
    })
  },
  onDragEnter: () => {
    groupIdUnderPostBeingDragged.value = props.group.id
    if (!isDraggingPostInNonManuallySortedWall.value) {
      closestEdgeToPointerForSection.value = 'bottom'
    }
  },
  onDragLeave: () => {
    closestEdgeToPointerForSection.value = undefined
  },
  canDrop: () => {
    if (!isAppUsing('pdndSurfaceStream')) return false
    // If the wall is non-manually sorted, we allow dropping posts into the section
    if (isDraggingPostInNonManuallySortedWall.value) return true
    const posts = allSortedPostsByGroup.value[props.group.id]
    if (posts && posts.length > 0) return false
    // If the wall is manually sorted, we allow dropping posts into the section if the section is empty
    return true
  },
})
// #endregion

// #region Drag preview width
const dragPreviewWidth = computed(() => {
  const padding = 16
  if (isSmallerThanTabletPortrait.value) return windowWidth.value - padding
  // Standard post width for stream
  const standardPostWidth = 540
  if (isPostSizeStandard.value) return standardPostWidth
  // Wide post width for stream
  const widePostWidth = 748
  return widePostWidth
})
// #endregion
</script>

<template>
  <section
    :id="'section-' + group.id"
    ref="sectionElementRef"
    :key="group.id"
    :data-id="group.id"
    :data-rank="group.sort_index"
    :class="[
      'flex',
      'flex-col',
      'w-full',
      'items-start tablet-portrait:items-center',
      {
        'surface-section': wishGroupBy === WallWishGroupByTypes.Section,
      },
      'relative',
    ]"
  >
    <!-- DRAG AND DROP OVERLAY -->
    <div
      v-if="canShowDraggingOverlayOnGroup(group.id)"
      :class="[
        'absolute -mt-4 z-underneath',
        'border-solid border-2 rounded-xl shadow-elevation-2',
        'pointer-events-none',
        {
          'border-grape-500 bg-modal-overlay-grape-alpha-20': backgroundLuminance === 'light',
          'border-canary-500 bg-modal-overlay-canary-alpha-20': backgroundLuminance === 'dark',
        },
      ]"
      :style="{ height: `${overlayHeight}px`, width: isSmallerThanTabletPortrait ? '100%' : `${overlayWidth}px` }"
    />
    <!-- GROUP HEADER -->
    <SurfacePostGroupHeaderSection
      v-if="isAppUsing('groupPostsV2') && !isGroupByNone"
      ref="groupHeaderSectionRef"
      :section="group"
      :class="[
        // By design, this header max width is post width - 6px.
        // Since the parent sets align-items: center, we use
        // negative margin here to pull the header a bit to the
        // left (right in RTL).
        'tablet-portrait:-ms-1.5',
        'w-full',
        // On mobile viewport, the header extends all the way to
        // the left (right in RTL) so we need to subtract 8px
        // (equal to padding).
        isPostSizeStandard && 'tablet-portrait:max-w-[calc(540px-6px)] max-w-[calc(100%-6px-8px)]',
        isPostSizeWide && 'tablet-portrait:max-w-[calc(748px-6px)] max-w-[calc(100%-6px-24px)]',
        isCurrentSectionBeingDragged && 'opacity-50',
      ]"
      :hide-buttons="isASectionDragging"
    />
    <SurfacePostGroupHeaderSectionOld
      v-if="!isAppUsing('groupPostsV2') && wishGroupBy === WallWishGroupByTypes.Section"
      ref="groupHeaderSectionRef"
      :section="group"
      :class="[
        // By design, this header max width is post width - 6px.
        // Since the parent sets align-items: center, we use
        // negative margin here to pull the header a bit to the
        // left (right in RTL).
        'tablet-portrait:-ms-1.5',
        'w-full',
        // On mobile viewport, the header extends all the way to
        // the left (right in RTL) so we need to subtract 8px
        // (equal to padding).
        isPostSizeStandard && 'tablet-portrait:max-w-[calc(540px-6px)] max-w-[calc(100%-6px-8px)]',
        isPostSizeWide && 'tablet-portrait:max-w-[calc(748px-6px)] max-w-[calc(100%-6px-24px)]',
        isCurrentSectionBeingDragged && 'opacity-50',
      ]"
      :hide-buttons="isASectionDragging"
    />

    <OzDropIndicatorLine
      v-if="closestEdgeToPointerForSectionHeader || closestEdgeToPointerForSection"
      :edge="closestEdgeToPointerForSectionHeader || closestEdgeToPointerForSection"
      :gap="40"
      :class="[
        'my-1',
        isPostSizeStandard && 'tablet-portrait:max-w-[calc(540px-6px)] max-w-[calc(100%-6px-8px)]',
        isPostSizeWide && 'tablet-portrait:max-w-[calc(748px-6px)] max-w-[calc(100%-6px-24px)]',
        'mx-auto',
      ]"
    />

    <!-- POSTS IN GROUP -->
    <div
      v-show="!isASectionDragging"
      :id="`group-posts-${group.id}`"
      :data-id="group.id"
      :class="[
        'flex',
        'flex-col',
        'items-center',
        'space-y-2 tablet-portrait:space-y-4',
        'box-border',
        group.id === WallWishGroupByTypes.None ? 'mt-0' : 'mt-4',
        isPostSizeStandard && 'px-2 tablet-portrait:px-0',
        isPostSizeWide && 'px-2 tablet-portrait:px-6',
        'w-full',
        'group-posts',
      ]"
    >
      <template v-for="post in allSortedPostsByGroup[group.id]">
        <SurfaceLazy
          v-show="shouldDisplayPost(post.cid)"
          :key="post.cid"
          :data-cid="post.cid"
          :data-id="post.id"
          :min-height="minHeight"
          :min-width="minWidth"
          :class="['wish-wrapper', 'w-full', 'flex', 'justify-center']"
          :render-immediately="!isAppUsing('lazyRenderPosts')"
        >
          <SurfacePost
            v-if="!isAppUsing('pdndSurfaceStream')"
            :class="[
              // Exclude this component from using styles in app/assets/stylesheets/3/surface/show/layouts/stream.scss
              'wish-v2',
              'w-full',
              isPostSizeStandard && 'tablet-portrait:max-w-135',
              isPostSizeWide && 'tablet-portrait:max-w-187',
            ]"
            :index="post.id"
            :post="post"
            :post-reaction-ids="post.id ? reactionIdsByPostId[post.id] || [] : []"
            :post-comment-ids="post.id ? commentIdsByPostId[post.id] || [] : []"
          />
          <SurfacePostDraggable
            v-else
            :class="[
              // Exclude this component from using styles in app/assets/stylesheets/3/surface/show/layouts/stream.scss
              'wish-v2',
              'w-full',
              isPostSizeStandard && 'tablet-portrait:max-w-135',
              isPostSizeWide && 'tablet-portrait:max-w-187',
            ]"
            :index="post.id"
            :post="post"
            :post-reaction-ids="post.id ? reactionIdsByPostId[post.id] || [] : []"
            :post-comment-ids="post.id ? commentIdsByPostId[post.id] || [] : []"
            :drag-element-type="SURFACE_STREAM_POST_DRAGGABLE_TYPE"
            :drop-element-type="SURFACE_STREAM_POST_DRAGGABLE_TYPE"
            axis="vertical"
            :gap="16"
            :should-compress="isAPostDragging"
            :is-draggable="!shouldDisableDraggingPost"
            :drag-preview-width="dragPreviewWidth"
            @on-drag-start="handlePostDragStart"
            @on-drop="handlePostDrop"
          />
        </SurfaceLazy>
      </template>
    </div>
  </section>
</template>
